import { NavLink, Outlet, useNavigate, useSearchParams, useLocation, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { canAccess, homeFor } from "@/lib/access";
import { isFleet, CONCRETE_ONLY_PATHS } from "@/lib/industry";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { usePlant } from "@/context/PlantContext";
import { useTheme, THEMES } from "@/context/ThemeContext";
import { Palette, Clapperboard, BadgeDollarSign, Menu } from "lucide-react";
import PresentBar from "@/components/PresentBar";
import AgreementGate from "@/components/AgreementGate";
import GatedOutlet from "@/components/GatedOutlet";
import { ROUTE_FEATURE, makeHasFeature } from "@/lib/tiers";
import { Lock as LockIcon, Layers } from "lucide-react";
import { presentingKey } from "@/lib/showcaseTour";
import WeatherAlertBanner from "@/components/WeatherAlertBanner";
import OfflineBanner from "@/components/OfflineBanner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  LayoutDashboard, Users, FlaskConical, Truck, PackageOpen, ClipboardList,
  FileText, BarChart3, Settings as Cog, LogOut, HardHat, MapPin, Coins, Link2,
  Sun, Moon, Factory, Monitor, Radio, User as UserIcon,
  TrendingUp, Wrench, Receipt, Construction, Inbox, ExternalLink, ShieldAlert, HeartPulse, Radar, CalendarDays, Sparkles,
  ChevronsLeft, ChevronsRight, Building2, LifeBuoy, Droplets, Handshake, CloudRain,
  Pencil, Check, X as XIcon, Printer, Scale,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import GadtechLogo from "@/components/GadtechLogo";
import SosWidget from "@/components/SosWidget";
import SpecAlertWatcher from "@/components/SpecAlertWatcher";
import { isDemoMode, exitDemoMode } from "@/components/DemoModeCard";
import { SA_ORG_KEY } from "@/lib/api";

/* GADTECH Dispatch™ v3.0 nav — matches the branded IA */
const NAV = [
  { to: "/",           label: "Dashboard",        icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/showcase",   label: "Demo Showcase",    icon: Clapperboard,    testid: "nav-showcase" },
  { to: "/war-room",   label: "War Room",         icon: Radar,           testid: "nav-war-room" },
  { to: "/executive",  label: "Executive",        icon: TrendingUp,      testid: "nav-executive" },
  { to: "/dispatch",   label: "Dispatch",         icon: Radio,           testid: "nav-dispatch" },
  { to: "/sales",      label: "Sales Schedule",   icon: CalendarDays,    testid: "nav-sales" },
  { to: "/customers",  label: "Customers",        icon: Users,           testid: "nav-customers" },
  { to: "/portal-requests", label: "Portal Inbox", icon: Inbox,          testid: "nav-portal-requests", badge: "portal-requests" },
  { to: "/leads",      label: "Leads",            icon: Sparkles,        testid: "nav-leads", superOnly: true },
  { to: "/pump-companies", label: "Pump Companies", icon: Construction,   testid: "nav-pump-companies" },
  { to: "/haulage",    label: "Haulage Contractors", icon: HardHat,       testid: "nav-haulage" },
  { to: "/partners",   label: "Partner Network",  icon: Handshake,       testid: "nav-partners" },
  { to: "/fleet",      label: "Fleet",            icon: Truck,           testid: "nav-fleet" },
  { to: "/mass-management", label: "Mass Management", icon: Scale,       testid: "nav-mass-management", fleetOnly: true },
  { to: "/plants",     label: "Plants (HQ)",      icon: Building2,       testid: "nav-plants" },
  { to: "/fleet-health", label: "Fleet Health",   icon: HeartPulse,      testid: "nav-fleet-health" },
  { to: "/maintenance",label: "Maintenance",      icon: Wrench,          testid: "nav-maintenance" },
  { to: "/drivers",    label: "Drivers",          icon: UserIcon,        testid: "nav-drivers" },
  { to: "/batcher",    label: "Batch Plant",      icon: Factory,         testid: "nav-batcher" },
  { to: "/mixes",      label: "Mix Designs",      icon: FlaskConical,    testid: "nav-mixes" },
  { to: "/materials",  label: "Materials",        icon: FlaskConical,    testid: "nav-materials", fleetOnly: true },
  { to: "/pricing",    label: "Pricing",          icon: Coins,           testid: "nav-pricing" },
  { to: "/surcharges", label: "Surcharges",       icon: BadgeDollarSign, testid: "nav-surcharges" },
  { to: "/map",        label: "GPS",              icon: MapPin,          testid: "nav-map" },
  { to: "/dockets",    label: "Delivery Dockets", icon: ClipboardList,   testid: "nav-dockets" },
  { to: "/slump-log",  label: "AI Slump Log",     icon: Droplets,        testid: "nav-slump-log" },
  { to: "/pour-warnings", label: "Pour Warnings", icon: CloudRain,       testid: "nav-pour-warnings" },
  { to: "/invoices",   label: "Accounts",         icon: FileText,        testid: "nav-invoices" },
  { to: "/statements", label: "Statements",       icon: Receipt,         testid: "nav-statements" },
  { to: "/inventory",  label: "Inventory",        icon: PackageOpen,     testid: "nav-inventory" },
  { to: "/cashsales",  label: "EFT Sales",         icon: Coins,           testid: "nav-cashsales" },
  { to: "/reports",    label: "Reports",          icon: BarChart3,       testid: "nav-reports" },
  { to: "/daily-summary", label: "Daily Summary", icon: TrendingUp,      testid: "nav-daily-summary" },
  { to: "/print-centre", label: "Print Centre",   icon: Printer,         testid: "nav-print-centre" },
  { to: "/live",       label: "Live Board",       icon: Monitor,         testid: "nav-live" },
  { to: "/settings/link", label: "ArmorBatch Link", icon: Link2,          testid: "nav-armorbatch-link" },
  { to: "/settings",   label: "Settings",         icon: Cog,             testid: "nav-settings" },
  // v3.6s · System Logs — admin-only "danger zone" for the live-launch
  // archive/reset and read-only historical browser. Hidden below Settings
  // and only rendered for the admin role (see filter in the sidebar).
  { to: "/system-logs", label: "System Logs",     icon: ShieldAlert,     testid: "nav-system-logs", superOnly: true },
  { to: "/organizations", label: "Organizations", icon: Building2,       testid: "nav-organizations", superOnly: true },
  { to: "/clients",    label: "Clients & Tiers",  icon: Layers,          testid: "nav-clients", superOnly: true },
  { to: "/support-inbox", label: "Support Inbox",  icon: LifeBuoy,        testid: "nav-support-inbox", superOnly: true, badge: "sos" },
];

/* Trial countdown — quiet at first, urgent in the last few days. */
function TrialBanner({ org }) {
  if (org?.plan !== "trial" || !org?.trial_ends_at) return null;
  const days = Math.ceil((new Date(org.trial_ends_at) - Date.now()) / 86400000);
  if (days < 0) return null;
  const urgent = days <= 3;
  return (
    <div data-testid="trial-banner"
      className={`px-3 md:px-4 py-2 flex items-center gap-2 flex-wrap border-b ${urgent ? "border-red-600 bg-red-950/40 text-red-200" : "border-industrial bg-surface text-muted-industrial"}`}>
      <span className={`mono text-[10px] uppercase tracking-widest font-black ${urgent ? "text-red-300" : "text-primary-orange"}`}>
        Trial · {days === 0 ? "last day" : `${days} day${days === 1 ? "" : "s"} left`}
      </span>
      <span className="text-[11px] hidden sm:inline">
        {urgent ? "Your plant switches off when the trial ends — talk to us to keep it running." : "Everything's unlocked. $100 USD per truck per month after the trial."}
      </span>
      <a href="mailto:admin@armorbatch.com?subject=ArmorBatch%20%E2%80%94%20continue%20after%20trial" data-testid="trial-banner-contact"
        className="ml-auto mono text-[10px] uppercase tracking-widest font-black px-2.5 py-1 border border-current hover:opacity-80">
        Continue
      </a>
    </div>
  );
}

export default function AppShell() {
  const { user, logout, org, refreshOrg } = useAuth();
  const hasFeature = makeHasFeature(org, user);
  const { plants, plantId, setPlantId, currentPlant } = usePlant();
  const [saOrgs, setSaOrgs] = useState([]);
  const saActiveOrg = localStorage.getItem(SA_ORG_KEY) || user?.org_id || "";
  // Inside a demo (TasMulch or the concrete Sample Plant) the switcher locks to that one
  // org so a presenter can't accidentally jump into a real customer account mid-demo.
  const inDemoOrg = saActiveOrg === "org_demo_sample" || saActiveOrg === "org_demo_fleet";
  const visibleOrgs = inDemoOrg ? saOrgs.filter((o) => o.id === saActiveOrg) : saOrgs;
  const canRenameDemo = inDemoOrg && user?.is_super_admin;
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const saveDemoName = async () => {
    const name = nameDraft.trim();
    if (!name) { setRenaming(false); return; }
    try {
      await api.post("/demo/rename", { org_id: saActiveOrg, name });
      await refreshOrg();
      toast.success("Demo name updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not rename the demo"); }
    finally { setRenaming(false); }
  };
  useEffect(() => {
    if (!user?.is_super_admin) return;
    api.get("/superadmin/orgs").then((r) => setSaOrgs(r.data)).catch(() => {});
  }, [user]);
  const switchOrg = async (id) => {
    if (!id || id === user?.org_id) localStorage.removeItem(SA_ORG_KEY); else localStorage.setItem(SA_ORG_KEY, id);
    if (id === "org_demo_sample" || id === "org_demo_fleet") localStorage.setItem("demo_mode", "1"); else localStorage.removeItem("demo_mode");
    localStorage.removeItem("plant_id");
    await refreshOrg();
    window.location.assign("/");
  };
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const location = useLocation();
  useEffect(() => { setMobileNav(false); }, [location.pathname]);
  const [inboxCount, setInboxCount] = useState(0);
  const [sosCount, setSosCount] = useState(0);
  const [searchParams] = useSearchParams();
  // v3.4n · Pop-out mode — open Batch Plant (or any page) in a chromeless
  // window that can be dragged to a second monitor. `?popout=1` strips the
  // sidebar + header so it acts like a dedicated console.
  const isPopout = searchParams.get("popout") === "1";
  const isPresenting = !!presentingKey();

  // v3.16 — Collapsible sidebar. Persisted so the layout survives reloads.
  // On small laptops / iPad landscape the rail starts collapsed to hand the width back to the
  // work area. The toggle still wins once it has been used on this device.
  const [navCollapsed, setNavCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem("gt.nav.collapsed");
      if (saved != null) return saved === "1";
      return window.innerWidth < 1440;
    } catch (_) { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("gt.nav.collapsed", navCollapsed ? "1" : "0"); } catch (_) {}
  }, [navCollapsed]);

  // Phone/tablet nav drawer. Closes itself whenever the route changes so a rep
  // taps once to pick a screen and lands straight on it.
  const [mobileNav, setMobileNav] = useState(false);

  const openPopout = (path) => {
    const w = 1400, h = 900;
    const left = (window.screen.availWidth - w) / 2;
    const top = (window.screen.availHeight - h) / 2;
    window.open(
      `${path}${path.includes("?") ? "&" : "?"}popout=1`,
      `gadtech-popout-${path}`,
      `width=${w},height=${h},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no`,
    );
  };

  // v3.4l — live count of portal requests needing staff action
  useEffect(() => {
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const r = await api.get("/portal-requests");
        if (!cancelled) setInboxCount(r.data.counts?.total || 0);
      } catch (_) { /* not authorised or offline */ }
    };
    fetchCount();
    const t = setInterval(fetchCount, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // SOS alert — super admins get a badge + toast (with chime) whenever a new ticket lands.
  useEffect(() => {
    if (!user?.is_super_admin) return undefined;
    let cancelled = false;
    let lastId = null;
    let first = true;
    const poll = async () => {
      try {
        const r = await api.get("/superadmin/support/open-count");
        if (cancelled) return;
        setSosCount(r.data.open || 0);
        const latest = r.data.latest;
        if (latest && latest.id !== lastId && !first) {
          try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            [0, 0.25].forEach((t) => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.connect(g); g.connect(ctx.destination); o.frequency.value = 880; g.gain.setValueAtTime(0.25, ctx.currentTime + t); g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + t + 0.2); o.start(ctx.currentTime + t); o.stop(ctx.currentTime + t + 0.2); });
          } catch (_) {}
          toast.error(`SOS ${latest.id} · ${latest.urgency?.toUpperCase()} — ${latest.org_name}`, {
            description: `${latest.user_name || "A user"} needs help (${latest.category}).`,
            duration: 30000,
            action: { label: "Open", onClick: () => nav("/support-inbox") },
          });
        }
        if (latest) lastId = latest.id;
        first = false;
      } catch (_) { /* offline */ }
    };
    poll();
    const t = setInterval(poll, 20000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user?.is_super_admin, nav]);

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  // Section gate — role defaults or admin-nominated pages; blocked pages bounce to the user's home.
  const allowedHere = canAccess(user, location.pathname) || ["/organizations", "/support-inbox"].includes(location.pathname) || (location.pathname === "/system-logs" && user?.is_super_admin);
  if (user && !allowedHere) {
    const home = homeFor(user);
    if (home !== location.pathname) return <Navigate to={home} replace />;
  }

  // In pop-out mode, render only the routed page — no sidebar, no top header.
  // Dispatcher can then drag the browser window onto another monitor / TV.
  if (isPopout || isPresenting) {
    return (
      <div className="min-h-screen bg-[rgb(var(--bg))]" data-testid={isPresenting ? "app-present-shell" : "app-popout-shell"}>
        {isPresenting && <PresentBar />}
        <OfflineBanner />
        <WeatherAlertBanner scope="staff" />
        <GatedOutlet />
      </div>
    );
  }

  return (
    <div className="h-screen flex overflow-hidden bg-[rgb(var(--bg))]">
      <AgreementGate />
      {/* Sidebar — collapses to icon rail (w-14) when navCollapsed */}
      <aside
        data-testid="app-sidebar"
        data-collapsed={navCollapsed ? "1" : "0"}
        className={`shrink-0 h-screen min-h-0 border-r border-industrial bg-surface flex flex-col duration-200 transition-transform md:transition-[width]
          max-md:fixed max-md:inset-y-0 max-md:left-0 max-md:z-50 max-md:w-64 ${mobileNav ? "max-md:translate-x-0 max-md:shadow-2xl" : "max-md:-translate-x-full"}
          ${navCollapsed ? "md:w-14" : "md:w-64"}`}
      >
        <div className={`border-b border-industrial ${navCollapsed ? "px-2 py-4" : "px-5 py-5"}`}>
          {navCollapsed ? (
            <div className="flex items-center justify-center">
              <GadtechLogo className="h-6" data-testid="app-sidebar-logo" />
            </div>
          ) : (
            <>
              <GadtechLogo className="h-12" data-testid="app-sidebar-logo" />
              {renaming ? (
                <div className="mt-1.5 flex items-center gap-1">
                  <input autoFocus data-testid="demo-name-input" value={nameDraft} onChange={(e) => setNameDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") saveDemoName(); if (e.key === "Escape") setRenaming(false); }}
                    maxLength={40} placeholder="Company name"
                    className="w-32 bg-transparent border border-primary-orange/60 mono text-[11px] text-primary-orange px-1.5 py-0.5 rounded-none focus:outline-none" />
                  <button type="button" data-testid="demo-name-save" onClick={saveDemoName} className="text-emerald-500 hover:text-emerald-400 p-0.5"><Check className="w-3.5 h-3.5" /></button>
                  <button type="button" data-testid="demo-name-cancel" onClick={() => setRenaming(false)} className="text-muted-industrial hover:text-red-500 p-0.5"><XIcon className="w-3.5 h-3.5" /></button>
                </div>
              ) : (
                <div className="label-tag text-primary-orange mt-1.5 flex items-center gap-1.5 max-w-full">
                  <span className="truncate" data-testid="app-sidebar-orgname">{org?.name || "ArmorBatch Global"}</span>
                  {canRenameDemo && (
                    <button type="button" data-testid="demo-name-edit" title="Rename this demo for your prospect"
                      onClick={() => { setNameDraft(org?.name || ""); setRenaming(true); }}
                      className="shrink-0 text-muted-industrial hover:text-primary-orange"><Pencil className="w-3 h-3" /></button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        <nav className="flex-1 py-3 overflow-y-auto overflow-x-hidden">
          {NAV.filter(({ to, adminOnly, superOnly, fleetOnly }) => (!adminOnly || user?.role === "admin") && (!superOnly || user?.is_super_admin) && (adminOnly || superOnly || canAccess(user, to)) && !(user?.demo_only && ["/system-logs", "/settings/link"].includes(to)) && !(isFleet(org) && CONCRETE_ONLY_PATHS.includes(to)) && !(fleetOnly && !isFleet(org))).map(({ to, label, icon: Icon, testid, badge, adminOnly }) => (
            <div key={to} className={`relative group ${adminOnly ? "mt-2 border-t border-industrial/60 pt-2" : ""}`}>
              <NavLink
                to={to}
                end={to === "/"}
                data-testid={testid}
                title={navCollapsed ? label : undefined}
                className={({ isActive }) =>
                  `flex items-center gap-3 text-sm border-l-2 transition-colors ${
                    navCollapsed ? "px-3 py-2.5 justify-center" : "px-5 py-2.5 pr-9"
                  } ${
                    isActive
                      ? "border-primary-orange bg-surface-2 text-ink"
                      : "border-transparent text-muted-industrial hover:text-ink hover:bg-surface-2"
                  } ${ROUTE_FEATURE[to] && !hasFeature(ROUTE_FEATURE[to]) ? "opacity-45 hover:opacity-70" : ""}`
                }
              >
                <Icon className="w-4 h-4 shrink-0" strokeWidth={2} />
                {!navCollapsed && (
                  <span className="uppercase tracking-wider text-xs font-semibold flex-1">{label}</span>
                )}
                {!navCollapsed && ROUTE_FEATURE[to] && !hasFeature(ROUTE_FEATURE[to]) && (
                  <LockIcon className="w-3 h-3 text-primary-orange shrink-0" data-testid={`nav-lock-${testid}`} />
                )}
                {badge === "sos" && sosCount > 0 && (
                  <span
                    className={`mono text-[10px] font-bold rounded-sm bg-red-600 text-white animate-pulse ${navCollapsed ? "absolute top-1 right-1 px-1 py-0" : "px-1.5 py-0.5"}`}
                    data-testid="sos-inbox-badge"
                  >
                    {sosCount}
                  </span>
                )}
                {badge === "portal-requests" && inboxCount > 0 && (
                  <span
                    className={`mono text-[10px] font-bold rounded-sm bg-primary-orange text-black ${
                      navCollapsed
                        ? "absolute top-1 right-1 px-1 py-0"
                        : "px-1.5 py-0.5"
                    }`}
                    data-testid="portal-inbox-badge"
                  >
                    {inboxCount}
                  </span>
                )}
              </NavLink>
              {!navCollapsed && (
                <button
                  type="button"
                  data-testid={`popout-${testid}`}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); openPopout(to); }}
                  title={`Pop out ${label} in a new window`}
                  className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 text-muted-industrial hover:text-primary-orange p-1 transition-opacity"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </nav>
        <div className={`border-t border-industrial ${navCollapsed ? "p-2" : "p-4"}`}>
          {!navCollapsed && (
            <div className="mb-3">
              <div className="label-tag">Signed In</div>
              <div className="text-sm font-semibold truncate" data-testid="current-user-name">{user?.name}</div>
              <div className="text-xs text-muted-industrial mono uppercase">{user?.role?.replace("_", " ")}</div>
            </div>
          )}
          <Button
            data-testid="logout-btn"
            variant="outline"
            size="sm"
            onClick={handleLogout}
            title={navCollapsed ? "Sign out" : undefined}
            className={`w-full rounded-lg border-industrial bg-transparent hover:bg-surface-2 text-ink uppercase tracking-wider text-xs ${navCollapsed ? "px-0 justify-center" : ""}`}
          >
            <LogOut className={`w-3.5 h-3.5 ${navCollapsed ? "" : "mr-2"}`} />
            {!navCollapsed && "Sign Out"}
          </Button>
        </div>
      </aside>

      {/* Phone/tablet: the nav is an off-canvas drawer. A fixed 256px rail left a
          390px iPhone with ~130px of usable page, which made every screen unusable. */}
      {mobileNav && (
        <button type="button" aria-label="Close navigation" data-testid="mobile-nav-backdrop"
          onClick={() => setMobileNav(false)} className="md:hidden fixed inset-0 z-40 bg-black/70" />
      )}

      {/* Main */}
      <main className="flex-1 min-w-0 min-h-0 flex flex-col">
        <TrialBanner org={org} />
        <header className="h-14 border-b border-industrial bg-surface px-3 md:px-4 flex items-center justify-between gap-2" data-testid="app-topbar">
          <div className="flex items-center gap-2 md:gap-3 min-w-0">
            <button
              type="button"
              data-testid="mobile-nav-toggle"
              onClick={() => setMobileNav(true)}
              title="Menu"
              className="md:hidden inline-flex items-center justify-center w-11 h-11 border border-industrial text-muted-industrial active:text-primary-orange active:border-primary-orange"
            >
              <Menu className="w-5 h-5" />
            </button>
            <button
              type="button"
              data-testid="sidebar-toggle"
              onClick={() => setNavCollapsed((v) => !v)}
              title={navCollapsed ? "Expand navigation (⌘\\)" : "Collapse navigation (⌘\\)"}
              className="hidden md:inline-flex items-center justify-center w-7 h-7 border border-industrial text-muted-industrial hover:text-primary-orange hover:border-primary-orange transition-colors"
            >
              {navCollapsed ? <ChevronsRight className="w-3.5 h-3.5" /> : <ChevronsLeft className="w-3.5 h-3.5" />}
            </button>
            <div className="label-tag hidden xl:block" data-testid="app-header-tagline">ARMORBATCH GLOBAL · Operations Console</div>
            {user?.demo_only && (
              <span data-testid="demo-readonly-badge" title="Read-only demo of the ArmorBatch Sample Plant"
                className="mono text-[10px] uppercase tracking-widest px-2 py-1 bg-emerald-500 text-black font-bold">
                ● Demo · Read-only · Sample Plant
              </span>
            )}
            {isDemoMode() && !user?.demo_only && (
              <button type="button" data-testid="demo-mode-badge" onClick={exitDemoMode} title="Demo Mode is ON — click to exit and return to your real data"
                className="mono text-[10px] uppercase tracking-widest px-2 py-1 bg-sky-500 text-black font-bold animate-pulse hover:animate-none">
                ● DEMO MODE · click to exit
              </button>
            )}
            {user?.is_super_admin && saOrgs.length > 0 && (
              <Select value={saActiveOrg} onValueChange={switchOrg}>
                <SelectTrigger data-testid="org-switcher" className="h-8 rounded-lg bg-transparent border-primary-orange/60 mono text-xs w-52 text-primary-orange">
                  <SelectValue placeholder="Organization" />
                </SelectTrigger>
                <SelectContent className="rounded-lg bg-surface border-industrial">
                  {visibleOrgs.map((o) => (
                    <SelectItem key={o.id} value={o.id} data-testid={`org-option-${o.slug}`}>{o.name}{o.active ? "" : " (suspended)"}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {plants.length > 0 && (
              <Select value={plantId} onValueChange={setPlantId}>
                <SelectTrigger data-testid="plant-selector" className="h-8 rounded-lg bg-transparent border-industrial mono text-xs w-32 md:w-56 hover:border-primary-orange">
                  <SelectValue placeholder="Select plant" />
                </SelectTrigger>
                <SelectContent className="rounded-lg bg-surface border-industrial">
                  {plants.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`plant-option-${p.code}`}>
                      {p.code} · {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="flex items-center gap-2 md:gap-3 shrink-0">
            <div className="hidden md:block"><ThemePicker /></div>
            <button
              type="button"
              data-testid="theme-toggle"
              onClick={toggle}
              title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              className="inline-flex items-center gap-1.5 border border-industrial hover:border-primary-orange text-muted-industrial hover:text-primary-orange px-2 py-1.5 mono text-[10px] uppercase tracking-widest rounded-md"
            >
              {theme === "dark" ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
              <span className="hidden sm:inline">{theme === "dark" ? "Light" : "Dark"}</span>
            </button>
            <div className="w-2 h-2 rounded-full bg-primary-orange pulse-orange" />
            <span className="label-tag hidden sm:inline" data-testid="active-plant-label">
              LIVE · {currentPlant?.code || "PLANT"}
            </span>
          </div>
        </header>
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
          <ImpersonationBanner />
          <OfflineBanner />
          <WeatherAlertBanner scope="staff" />
          <GatedOutlet />
        </div>
      </main>
      <SosWidget locked={!hasFeature("sos_emergency")} />
      {hasFeature("advanced_reporting") && <SpecAlertWatcher />}
    </div>
  );
}

/**
 * v3.5v — Colour palette picker. Dropdown swatch grid so the user can
 * flip between Light / Dark / Midnight / Emerald / Slate / Concrete / Amber
 * without a redeploy. Persisted via ThemeContext + localStorage.
 */
function ThemePicker() {
  const { theme, setTheme } = useTheme();
  const active = THEMES.find((t) => t.id === theme) || THEMES[0];
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="theme-picker-trigger"
          title="Change colour theme"
          className="inline-flex items-center gap-1.5 border border-industrial hover:border-primary-orange text-muted-industrial hover:text-primary-orange px-2 py-1.5 mono text-[10px] uppercase tracking-widest rounded-md"
        >
          <Palette className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Theme</span>
          <span
            className="w-3 h-3 rounded-full border border-white/40 shadow-inner"
            style={{ background: active.swatch }}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="rounded-none border-industrial bg-surface p-2 w-64" data-testid="theme-picker-menu">
        <div className="mono text-[9px] uppercase tracking-[0.28em] text-muted-industrial mb-1.5 px-1">Colour palette</div>
        <div className="grid grid-cols-2 gap-1">
          {THEMES.map((t) => {
            const isActive = t.id === theme;
            return (
              <button
                key={t.id}
                type="button"
                data-testid={`theme-option-${t.id}`}
                onClick={() => setTheme(t.id)}
                title={t.hint}
                className={`text-left flex items-center gap-2 border p-1.5 mono text-[10px] uppercase tracking-widest transition-colors ${
                  isActive
                    ? "border-primary-orange bg-primary-orange/10 text-primary-orange"
                    : "border-industrial hover:border-primary-orange text-ink"
                }`}
              >
                <span
                  className="w-6 h-6 border border-white/30 shrink-0 shadow-sm"
                  style={{ background: `linear-gradient(135deg, ${t.bg} 0%, ${t.bg} 55%, ${t.swatch} 55%, ${t.swatch} 100%)` }}
                />
                <span className="min-w-0">
                  <span className="block font-black truncate">{t.label}</span>
                  <span className="block text-[8px] tracking-normal normal-case text-muted-industrial truncate">{t.hint}</span>
                </span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
